import React from 'react'
import type { TFunction } from 'i18next'
import type { Section } from './constants'

// URLパラメータ操作のユーティリティ関数群
export const urlParamsUtils = {
  // URLクエリパラメータから指定セクションの選択済みタグを取得
  getTagsFromUrl: (sectionId: string, sections: Section[]): string[] => {
    const params = new URLSearchParams(window.location.search)
    const section = sections.find((s) => s.id === sectionId)
    if (!section) return []

    return section.tags
      .filter((tag) => {
        const values = params.get(tag.key)?.split(',') || []
        return values.includes(tag.value)
      })
      .map((tag) => `${tag.key}:${tag.value}`)
  },
  // 現在のURLパラメータを取得
  getCurrentParams: (): URLSearchParams => {
    return new URLSearchParams(window.location.search)
  },

  // パラメータを更新（複数の更新を一括で行う）
  updateParams: (params: URLSearchParams, updates: Record<string, string | string[] | null>): URLSearchParams => {
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        params.delete(key)
      } else if (Array.isArray(value)) {
        if (value.length > 0) {
          params.set(key, value.join(','))
        } else {
          params.delete(key)
        }
      } else {
        params.set(key, value)
      }
    })
    return params
  },

  // ページ番号をリセット
  resetPageNumber: (params: URLSearchParams): URLSearchParams => {
    if (params.has('page')) {
      params.set('page', '1')
    }
    return params
  },

  // パラメータを使用してナビゲート
  navigateWithParams: (params: URLSearchParams, basePath: string = '/'): void => {
    const queryString = params.toString()
    const newUrl = queryString ? `${basePath}?${queryString}` : basePath
    window.location.href = newUrl
  },

  // keyごとにvalueをまとめる（複数セクションからのタグ選択に対応）
  mergeTagsByKey: (
    allSelectedTags: Array<{ sectionId: string; selected: string[] }>,
    _sections: Section[],
  ): Map<string, Set<string>> => {
    const keyToValues = new Map<string, Set<string>>()

    allSelectedTags.forEach(({ selected }) => {
      selected.forEach((keyValuePair) => {
        const [key, value] = keyValuePair.split(':')
        if (!keyToValues.has(key)) {
          keyToValues.set(key, new Set())
        }
        keyToValues.get(key)!.add(value)
      })
    })

    return keyToValues
  },

  // フィルター関連のパラメータをクリア
  clearFilterParams: (params: URLSearchParams, filterKeys: Set<string>): URLSearchParams => {
    Array.from(params.keys()).forEach((key) => {
      if (filterKeys.has(key)) {
        params.delete(key)
      }
    })
    return params
  },
}

// 後方互換性のため
export const getTagsFromUrl = urlParamsUtils.getTagsFromUrl

// モーダル背景スクロール制御フック
export const useModalScrollLock = (isOpen: boolean) => {
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }

    // クリーンアップ関数で元に戻す
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [isOpen])
}

export const stayDayOptions = (t: TFunction) => {
  return [
    {
      id: 1,
      name: t('top.modal_search.within_a_week'),
      value: '6',
    },
    {
      id: 2,
      name: '1' + t('top.modal_search.week'),
      value: '7',
    },
    {
      id: 3,
      name: '2' + t('top.modal_search.week'),
      value: '14',
    },
    {
      id: 4,
      name: '3' + t('top.modal_search.week'),
      value: '21',
    },
    {
      id: 5,
      name: '1' + t('top.modal_search.month_number_from'),
      value: '27',
    },
  ]
}

export const regionOptions = (t: TFunction) => {
  return [
    {
      id: 0,
      name: t('top.modal_search.search_region.hokkaido'),
      value: '北海道',
    },
    {
      id: 1,
      name: t('top.modal_search.search_region.tohoku'),
      value: '東北',
    },
    {
      id: 2,
      name: t('top.modal_search.search_region.hokuriku_koshinetsu'),
      value: '北陸・甲信越',
    },
    {
      id: 3,
      name: t('top.modal_search.search_region.kanto'),
      value: '関東',
    },
    {
      id: 4,
      name: t('top.modal_search.search_region.tokai_kinki'),
      value: '東海・近畿',
    },
    {
      id: 5,
      name: t('top.modal_search.search_region.chugoku_shikoku'),
      value: '中国・四国',
    },
    {
      id: 6,
      name: t('top.modal_search.search_region.kyushu_okinawa'),
      value: '九州・沖縄',
    },
  ]
}

export const acceptClassOptions = (t: TFunction) => {
  return [
    {
      id: 0,
      name: t('top.modal_search.class.no_attendance'),
      value: 'no_enrollment',
    },
    {
      id: 1,
      name: t('top.modal_search.class.0_year_old_class'),
      value: t('top.modal_search.value.0_year_old_class'),
    },
    {
      id: 2,
      name: t('top.modal_search.class.1_year_old_class'),
      value: t('top.modal_search.value.1_year_old_class'),
    },
    {
      id: 3,
      name: t('top.modal_search.class.2_years_old_class'),
      value: t('top.modal_search.value.2_years_old_class'),
    },
    {
      id: 4,
      name: t('top.modal_search.class.3_years_old_class'),
      value: t('top.modal_search.value.3_years_old_class'),
    },
    {
      id: 5,
      name: t('top.modal_search.class.4_years_old_class'),
      value: t('top.modal_search.value.4_years_old_class'),
    },
    {
      id: 6,
      name: t('top.modal_search.class.5_years_old_class'),
      value: t('top.modal_search.value.5_years_old_class'),
    },
  ]
}

export type KeyDataSearch =
  | 'maximum_number_of_adults'
  | 'maximum_number_of_children'
  | 'no_car'
  | 'stay_day'
  | 'plan_month'
  | 'accept_class'
  | 'category_name'
  | 'number_of_preschoolers'
  | 'number_of_students'
  | 'number_of_preschoolers_1'
  | 'number_of_students_1'
  | 'number_of_study_abroad'

export type ChildrenType = {
  id: number
  name: string
  value: string
}

export type ChildBirthdate = { year: string; month: string; day: string }

export type DataSearchType = {
  maximum_number_of_adults: number
  maximum_number_of_children: number
  accept_class: ChildrenType[]
  no_car: string
  stay_day: string[]
  plan_month: string[]
  text_search: string
  category_name: string[]
  number_of_preschoolers: number
  number_of_students: number
  number_of_preschoolers_1: number
  number_of_students_1: number
  number_of_study_abroad: number
  birthdays: ChildBirthdate[]
  start_date: string
  end_date: string
}

/**
 * maximum_number_of_children を算出する共通関数
 * 国内・JPE共通で使用
 */
export const calcMaxNumberOfChildren = (
  numberOfStudyAbroad: number,
  numberOfPreschoolers1: number,
  numberOfStudents1: number,
): number => numberOfStudyAbroad + numberOfPreschoolers1 + numberOfStudents1

export const DATA_SEARCH_INIT: DataSearchType = {
  maximum_number_of_adults: 2,
  maximum_number_of_children: 1,
  accept_class: [],
  no_car: '',
  stay_day: [],
  plan_month: [],
  text_search: '',
  category_name: [],
  number_of_preschoolers: 0,
  number_of_students: 0,
  number_of_preschoolers_1: 0,
  number_of_students_1: 0,
  number_of_study_abroad: 1,
  birthdays: [{ year: '', month: '', day: '' }],
  start_date: '',
  end_date: '',
}
